import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Assignment } from './assignment';
import { AnswerService } from './answers.service';

interface AssignmentResponse {
  user: any;
  assignments: Assignment[];
}

@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  constructor(private http: HttpClient,
    private answerService: AnswerService,
  ) {}

  async getAssignmentsForUser(): Promise<AssignmentResponse> {
    return await this.http.get<AssignmentResponse>(`user/assignments`).toPromise();
  }
  
  async assignTasks(payload: {
    courseId: string;
    studentIds: string[];
    exerciseId: string;
    questionCount?: number;
    deadline?: string;
  }): Promise<any> {
    return await this.http.post('assignments', payload).toPromise();
  }

  async updateAssignment(payload: {
    ids: string[];
    deadline: string;
  }): Promise<any> {
    return await this.http.post('assignments/update', payload).toPromise();
  }

  async deleteAssignments(ids: string[]): Promise<any> {
    return await this.http.request('delete', 'assignments/delete', {
      body: { ids },
    }).toPromise();
  }

  async getCompletionStatus(assignmentId: string): Promise<any> {
    return await this.http.get(`assignments/status?assignment=${assignmentId}`).toPromise();
  }

  async deleteForStudents(exerciseId: string, studentIds: string[]): Promise<any> {
    return await this.http.request('delete', 'assignments/delete-for-students', {
      body: { exerciseId, studentIds },
    }).toPromise();
  }

  public async getUpcomingAssignmentsForUser(userId: string): Promise<Assignment[]> {
    const response = await this.getAssignmentsForUser();
  
    const allAssignments = response?.assignments || [];
  
    if (allAssignments.length === 0) {
      return [];
    }
  
    const results = await Promise.all(
      allAssignments.map(async assignment => {
        const answers = await this.answerService.getAnswersForAssignment(assignment._id);
        
        // For dialog interactive exercises, check if conversation is actually completed
        // by checking if completedAt is set, not just if an answer exists
        const dialogInteractiveAnswer = answers.find(a => 
          a.exerciseId === assignment.exerciseId && 
          (a as any).dialogInteractiveData
        );
        
        if (dialogInteractiveAnswer) {
          const diData = (dialogInteractiveAnswer as any).dialogInteractiveData;
          const completedAtRaw = diData?.completedAt;
          
          // More robust check for completedAt
          let isCompletedByDate = false;
          if (completedAtRaw) {
            if (completedAtRaw instanceof Date) {
              isCompletedByDate = !isNaN(completedAtRaw.getTime());
            } else if (typeof completedAtRaw === 'string') {
              isCompletedByDate = completedAtRaw.trim().length > 0 && !isNaN(Date.parse(completedAtRaw));
            } else if (typeof completedAtRaw === 'object' && completedAtRaw !== null) {
              // Handle MongoDB date objects in various formats
              // Check for MongoDB extended JSON format: { $date: "..." }
              if ((completedAtRaw as any).$date) {
                const dateValue = (completedAtRaw as any).$date;
                if (typeof dateValue === 'string') {
                  isCompletedByDate = !isNaN(Date.parse(dateValue));
                } else if (typeof dateValue === 'number') {
                  isCompletedByDate = !isNaN(new Date(dateValue).getTime());
                }
              } else {
                // Try to convert to string and parse
                const dateStr = String(completedAtRaw);
                // Check if it's not just "[object Object]"
                if (dateStr !== '[object Object]') {
                  const parsed = Date.parse(dateStr);
                  isCompletedByDate = !isNaN(parsed);
                } else {
                  // Try to access common date properties
                  const timestamp = (completedAtRaw as any).getTime?.() || 
                                  (completedAtRaw as any).valueOf?.() ||
                                  (completedAtRaw as any).timestamp;
                  if (timestamp) {
                    isCompletedByDate = !isNaN(new Date(timestamp).getTime());
                  }
                }
              }
            }
          }
          
          const isCompleted = isCompletedByDate;
          
          console.log('[AssignmentService] DialogInteractive completion check:', {
            assignmentId: assignment._id,
            exerciseId: assignment.exerciseId,
            isCompleted,
            isCompletedByDate,
            completedAtRaw,
            completedAtType: typeof completedAtRaw
          });
          
          // For dialog interactive, only count as answered if actually completed
          return {
            assignment,
            answeredCount: isCompleted ? 1 : 0,
            questionCount: 1 // Dialog interactive is treated as single unit
          };
        }
        
        return {
          assignment,
          answeredCount: answers.length,
          questionCount: assignment.questionCount
        };
      })
    );
  
    return results
      .filter(r => !r.assignment.done && r.answeredCount < r.questionCount)
      .map(r => r.assignment);
  }  
}
